import { styled } from '@mui/material/styles';
import { useState, type ComponentType, type SVGProps } from 'react';
import Tooltip from '@/components/Tooltip/Tooltip';
import xslotsLogo from '@/assets/xslots-studio-logo.png';
import xslotsLogoColored from '@/assets/xslots-studio-logo-colored.png';
import KoalaLogo from '@/assets/koala-studio-logo.svg?react';
import KoalaLogoColored from '@/assets/koala-studio-logo-colored.svg?react';
import RubyplayLogo from '@/assets/rubyplay-studio-logo.svg?react';
import RubyplayLogoColored from '@/assets/rubyplay-studio-logo-colored.svg?react';
import MadhatLogo from '@/assets/madhat-studio-logo.svg?react';
import MadhatLogoColored from '@/assets/madhat-studio-logo-colored.svg?react';

type GameProvider =
  | 'Koala Games'
  | 'Ruby Play'
  | 'XSlots'
  | 'Mad Hat';

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>;

const studioLogos: Record<
  GameProvider,
  | { kind: 'svg'; normal: SvgComponent; colored: SvgComponent }
  | { kind: 'raster'; normal: string; colored: string }
> = {
  'Koala Games': {
    kind: 'svg',
    normal: KoalaLogo,
    colored: KoalaLogoColored,
  },
  'Ruby Play': {
    kind: 'svg',
    normal: RubyplayLogo,
    colored: RubyplayLogoColored,
  },
  XSlots: {
    kind: 'raster',
    normal: xslotsLogo,
    colored: xslotsLogoColored,
  },
  'Mad Hat': {
    kind: 'svg',
    normal: MadhatLogo,
    colored: MadhatLogoColored,
  },
};

const GameStudioLogo = ({
  gameProvider,
}: {
  gameProvider?: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!gameProvider) {
    return null;
  }

  const logos = studioLogos[gameProvider as GameProvider];
  if (!logos) {
    return null;
  }

  return (
    <StudioContainer>
      <Tooltip title={`Studio: ${gameProvider}`}>
        <span style={{ display: 'inline-flex' }}>
          <LogoContainer
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label={`${gameProvider} studio logo`}
          >
            {logos.kind === 'svg' ? (
              <LogoSvg
                as={isHovered ? logos.colored : logos.normal}
                role="img"
                aria-label={`${gameProvider} studio logo`}
              />
            ) : (
              <LogoRasterSvg
                viewBox="0 0 20 20"
                role="img"
                aria-label={`${gameProvider} studio logo`}
              >
                <image
                  href={isHovered ? logos.colored : logos.normal}
                  width="20"
                  height="20"
                  preserveAspectRatio="xMidYMid meet"
                />
              </LogoRasterSvg>
            )}
          </LogoContainer>
        </span>
      </Tooltip>
    </StudioContainer>
  );
};

const StudioContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const LogoContainer = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const LogoSvg = styled('svg')({
  height: 20,
  width: 'auto',
  display: 'block',
});

const LogoRasterSvg = styled('svg')({
  height: 20,
  width: 20,
  display: 'block',
});

export default GameStudioLogo;
